import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast'; 

function Hero() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCareerToolsOpen, setIsCareerToolsOpen] = useState(false);
  const { isAuthenticated, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await signOut();
      setIsMenuOpen(false);
      toast.success('Signed out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to sign out');
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#2A1114]">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full z-0">
        <div className="absolute inset-0 bg-black opacity-50 z-10"></div> {/* Overlay for better text visibility */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute min-w-full min-h-full object-cover"
        >
          <source src="https://otfbpcicwhfqgupzvoyx.supabase.co/storage/v1/object/public/media/uploads/20250626_1245_City%20Skyline%20Elegance_simple_compose_01jypnh56seynv7v8gw78fac0f.mp4" type="video/mp4" />
          {/* Fallback image if video doesn't load */}
          <img 
            src="https://i.ibb.co/WvCD5ngM/TEZHLY-13.png" 
            alt="Background" 
            className="absolute min-w-full min-h-full object-cover" 
          />
        </video>
      </div>
      
      {/* Add Playfair Display font import to head */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
        `}
      </style>
      
      {/* Top Bar */}
      <div className="fixed top-4 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-center relative">
            {/* Centered Logo */}
            <Link to="/" className="absolute left-1/2 transform -translate-x-1/2">
              <div className="flex items-center">
                <img
                  src="https://i.ibb.co/PZRVzW7g/THE-TECH-FIRM-COMPANY-2.png"
                  alt="Tezhly"
                  className="h-12 w-auto object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.insertAdjacentHTML('afterend', '<span class="text-2xl font-bold text-white">Tezhly</span>');
                  }}
                />
              </div>
            </Link>
            
            {/* Menu Icon - positioned on the right */}
            <div className="absolute right-0">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-white hover:text-[#E5DED6] transition-colors"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              {isMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg py-1">
                  <div className="space-y-2">
                    {/* Essential Menu Items */}
                    <Link
                      to="/community"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Home
                    </Link>
 <Link
                      to="/prestige-labs"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Dashboard
                    </Link>                    
                    <Link
                      to="/events"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Events
                    </Link>
                    <Link
                      to="/blog"
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-50"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Blog
                    </Link>
                    <Link
                      className="block px-3 py-2 text-[#800020] hover:bg-gray-50 border-b border-gray-100"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      My Profile
                    </Link>
                    
                    {/* Other menu items... */}
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-3 py-2 text-red-600 hover:bg-gray-50 flex items-center"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Hero Content */}
      <div className="relative z-20 flex flex-col justify-center h-screen px-4 md:px-12 lg:px-20">
        <div className="max-w-4xl">
          <h1 
            className="text-5xl md:text-6xl text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
             The Career <br />
            Lab for <span className="italic">Professionals</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/90 mb-12 max-w-2xl">
         As a career lab, we help corporate professionals build their next chapter in an AI-driven world.
          </p>
          
          <Link
            to="/create-account"
            className="inline-block px-8 py-3 bg-[#081b1e] text-[#FFFFFF] text-lg font-medium rounded-none hover:bg-opacity-90 transition-all flex items-center space-x-2 max-w-xs"
          >
            <span>Get Started</span>
            <span className="text-xl">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export { Hero };